import { useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate or get session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('cartie_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('cartie_session_id', sessionId);
  }
  return sessionId;
};

// Event queue for batching
let eventQueue = [];
let flushTimeout = null;

const flushEvents = async () => {
  if (eventQueue.length === 0) return;
  
  const eventsToSend = [...eventQueue];
  eventQueue = [];
  
  try {
    await axios.post(`${API_URL}/api/analytics/track/batch`, eventsToSend);
  } catch (error) {
    console.error('Failed to send analytics:', error);
    // Re-queue failed events
    eventQueue = [...eventsToSend, ...eventQueue];
  }
};

// Debounced flush
const scheduleFlush = () => {
  if (flushTimeout) clearTimeout(flushTimeout);
  flushTimeout = setTimeout(flushEvents, 2000); // Flush every 2 seconds
};

export const useAnalytics = () => {
  const sessionId = useRef(getSessionId());
  
  // Get current user info
  const getUserInfo = useCallback(() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return {
        user_id: user.id || null,
        user_role: user.role || null
      };
    } catch {
      return { user_id: null, user_role: null };
    }
  }, []);

  // Track a single event
  const track = useCallback((eventType, eventData = {}) => {
    const { user_id, user_role } = getUserInfo();
    
    const event = {
      event_type: eventType,
      event_data: eventData,
      session_id: sessionId.current,
      user_id,
      user_role,
      timestamp: new Date().toISOString()
    };
    
    eventQueue.push(event);
    scheduleFlush();
  }, [getUserInfo]);

  // Track page view
  const trackPageView = useCallback((pageName, additionalData = {}) => {
    track('page_view', { page: pageName, ...additionalData });
  }, [track]);

  // Track feature click
  const trackFeatureClick = useCallback((featureName, additionalData = {}) => {
    track('feature_click', { feature: featureName, ...additionalData });
  }, [track]);

  // Track demo mode
  const trackDemoStart = useCallback(() => {
    track('demo_start', { timestamp: new Date().toISOString() });
  }, [track]);

  const trackDemoExplore = useCallback((feature) => {
    track('demo_explore', { feature, timestamp: new Date().toISOString() });
  }, [track]);

  // Track account connection
  const trackAccountConnect = useCallback((provider, success, errorMessage = null) => {
    const eventType = success ? 'account_connect_success' : 'account_connect_fail';
    track(eventType, { provider, error: errorMessage });
  }, [track]);

  // Track AI advisor usage
  const trackAIQuery = useCallback((queryLength, responseTime = null) => {
    track('ai_advisor_query', { query_length: queryLength, response_time: responseTime });
  }, [track]);

  // Track recommendation interactions
  const trackRecommendation = useCallback((action, recommendationId, type) => {
    const eventType = action === 'view' ? 'recommendation_viewed' : 'recommendation_applied';
    track(eventType, { recommendation_id: recommendationId, type });
  }, [track]);

  // Flush on unmount or page unload
  useEffect(() => {
    const handleUnload = () => {
      if (eventQueue.length > 0) {
        // Use sendBeacon for reliable delivery on page unload
        const data = JSON.stringify(eventQueue);
        navigator.sendBeacon(`${API_URL}/api/analytics/track/batch`, data);
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      flushEvents(); // Flush remaining events on unmount
    };
  }, []);

  return {
    track,
    trackPageView,
    trackFeatureClick,
    trackDemoStart,
    trackDemoExplore,
    trackAccountConnect,
    trackAIQuery,
    trackRecommendation
  };
};

// Standalone tracking functions for use outside React components
export const trackEvent = (eventType, eventData = {}) => {
  const sessionId = getSessionId();
  let user_id = null;
  let user_role = null;
  
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    user_id = user.id || null;
    user_role = user.role || null;
  } catch {}
  
  const event = {
    event_type: eventType,
    event_data: eventData,
    session_id: sessionId,
    user_id,
    user_role,
    timestamp: new Date().toISOString()
  };
  
  eventQueue.push(event);
  scheduleFlush();
};

// Pre-defined event trackers
export const trackLandingPageView = () => trackEvent('landing_page_view');
export const trackPricingPageView = () => trackEvent('pricing_page_view');
export const trackWaitlistClick = () => trackEvent('waitlist_click');
export const trackWaitlistSubmit = (data) => trackEvent('waitlist_submit', data);
export const trackDemoCTAClick = () => trackEvent('demo_cta_click');
export const trackLogin = () => trackEvent('login');
export const trackSignup = () => trackEvent('signup');
export const trackProviderSelectorShown = () => trackEvent('provider_selector_shown');
export const trackProviderSelected = (providers, demoMode) => trackEvent('provider_selected', { providers, demo_mode: demoMode });
export const trackShareStory = (channel) => trackEvent('share_story_click', { channel });
export const trackFounderDMClick = () => trackEvent('founder_dm_click');

// v34.91 (Feb 13 2026) — "What CARTIE can do RIGHT NOW" 3-card row tracking.
// Lets the founder see which of the 3 discovery entry points converts best
// (reality-check vs prompt-cost-predictor vs slack-bot).
export const trackNowCardClick = (cardId) => trackEvent('now_card_click', { card_id: cardId });

// v35.02 (Feb 17 2026) — `/connect` Quickstart Strip 3-card tracking.
// Founder can read /api/analytics/quickstart-breakdown to see whether Slack,
// Jira, or Datadog is the most popular "first integration" pitch.
export const trackQuickstartCardClick = (destId) =>
  trackEvent('quickstart_card_click', { dest_id: destId });

export default useAnalytics;
